import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Checkbox } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import type { VisibilityState } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";

import { ampli } from "@/config";
import { isSamsaraUsernameTag, type TagDefinition } from "@/types/driver";
import { zinc } from "./ui/colors";

export const COLUMN_LABELS: Record<string, string> = {
  name: "Driver",
  recentMessage: "Most Recent Driver Message",
  group: "Group",
  status: "Hands-free Availability",
  dutyStatus: "Duty Status",
  location: "Location",
  volume: "Volume",
  country: "Country",
  stateProvince: "State/Province",
};

const BASE_TOGGLEABLE_COLUMNS = Object.keys(COLUMN_LABELS).filter((key) => key !== "name");

export default function ColumnVisibilityDropdown({
  columnVisibility,
  onColumnVisibilityChange,
  tags = [],
}: {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void;
  tags?: TagDefinition[];
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const allColumns = useMemo(() => {
    const tagEntries = tags
      .filter((t) => !isSamsaraUsernameTag(t))
      .map((t) => ({ key: `tag:${t.id}`, label: t.label }));
    const baseEntries = BASE_TOGGLEABLE_COLUMNS.map((key) => ({ key, label: COLUMN_LABELS[key] }));
    return [...baseEntries, ...tagEntries];
  }, [tags]);

  const toggleColumn = (key: string, label: string) => {
    onColumnVisibilityChange((prev) => {
      const wasVisible = prev[key] ?? true;
      return { ...prev, [key]: !wasVisible };
    });
    const wasVisible = columnVisibility[key] ?? true;
    if (wasVisible) {
      ampli.hideColumn({ columnName: label });
    } else {
      ampli.showColumn({ columnName: label });
    }
  };

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          fontSize: "1rem",
          bgcolor: open ? zinc[600] : zinc[700],
          color: open ? "white" : zinc[300],
          "&:hover": { bgcolor: zinc[600], color: "white" },
          cursor: "pointer",
        }}
      >
        <ViewColumnIcon sx={{ fontSize: 20 }} />
        <span>Columns</span>
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              bgcolor: zinc[800],
              border: `1px solid ${zinc[600]}`,
              boxShadow: "0 0 0 4px rgba(0,0,0,0.5)",
              borderRadius: 1,
              minWidth: 200,
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {allColumns.map(({ key, label }) => {
              const checked = columnVisibility[key] ?? true;
              return (
                <Box
                  component="li"
                  key={key}
                  sx={{
                    "&:not(:last-child)": { mb: 0.5 },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ButtonBase
                    onClick={() => toggleColumn(key, label)}
                    sx={{
                      width: "100%",
                      px: 1,
                      py: 0.25,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      borderRadius: 1,
                      justifyContent: "flex-start",
                      color: checked ? "white" : zinc[400],
                      bgcolor: checked ? zinc[700] : "transparent",
                      "&:hover": { bgcolor: zinc[700] },
                      fontSize: "0.875rem",
                    }}
                  >
                    <Checkbox sx={{ p: 0 }} checked={checked} size="small" />
                    <Box>{label}</Box>
                  </ButtonBase>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
